import { spotlight } from "@mantine/spotlight";
import { useOrgHelper } from "@propelauth/react";
import {
  Activity,
  Bot,
  Building2,
  Calendar,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Download,
  HelpCircle,
  Home,
  Inbox,
  Mail,
  Search,
  Users,
  Wand2,
  Zap,
} from "lucide-react";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AvatarMenu from "./AvatarMenu";
import SidebarNavItem from "./SidebarNavItem";
import SidebarSection from "./SidebarSection";

interface LeftSideBarProps {
  views: any[];
  workspace: any;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

const LeftSideBar = ({ views, workspace, collapsed, setCollapsed }: LeftSideBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgHelper } = useOrgHelper();
  const params = useParams();

  useEffect(() => {
    const onHotkey = (event: KeyboardEvent) => {
      if (!event.key) return;
      const key = event.key.toLowerCase();
      if ((event.metaKey || event.ctrlKey) && key === "k") {
        event.preventDefault();
        spotlight.open();
      }
    };

    window.addEventListener("keydown", onHotkey);
    return () => window.removeEventListener("keydown", onHotkey);
  }, []);

  const orgs: any = orgHelper?.getOrgs();
  const selectedWorkspace: any = useMemo(
    () => orgs?.find((item: any) => item.orgMetadata.unique_identifier === params.workspaceId) || undefined,
    [orgs, params.workspaceId],
  );

  const defaultView = useMemo(
    () => views?.filter((item: any) => item?.is_precreated)?.reduce((acc: any, item: any) => {
      const next = { ...acc };
      next[item?.entity_relation] = item;
      return next;
    }, {} as any),
    [views],
  );

  const accountsViewId = useMemo(
    () => defaultView?.accounts?.id ?? views?.find((item: any) => item?.entity_relation === "accounts")?.id,
    [defaultView?.accounts?.id, views],
  );

  const contactsViewId = useMemo(
    () => defaultView?.users?.id ?? views?.find((item: any) => item?.entity_relation === "users")?.id,
    [defaultView?.users?.id, views],
  );

  const currentViewId = useMemo(() => {
    const match = location.pathname.match(/\/view\/([^/]+)/);
    return match?.[1] || "";
  }, [location.pathname]);

  const currentEntityType = useMemo(
    () => views?.find((item: any) => String(item?.id) === currentViewId)?.entity_relation,
    [currentViewId, views],
  );

  const isPathActive = (path: string) => location.pathname.includes(path);
  const isViewActive = (viewId?: string | number) => (viewId ? location.pathname.includes(`/view/${viewId}`) : false);

  const navigateToView = (viewId?: string | number) => {
    if (!viewId) return;
    navigate(`/home/${params.workspaceId}/view/${viewId}`);
  };

  return (
    <aside
      className={`${
        collapsed ? "w-20" : "w-[244px]"
      } relative flex h-screen flex-col border-r border-border-secondary bg-sidebar font-sans transition-all duration-300`}
    >
      <div className="px-3 py-3">
        <AvatarMenu
          name={selectedWorkspace?.orgName || "Hyperengage Team"}
          img={workspace?.workspace_logo}
          menu={!collapsed}
          desc="Free Plan"
        />
      </div>

      {!collapsed && (
        <div className="px-3 pb-3">
          <button
            type="button"
            onClick={() => spotlight.open()}
            className="w-full rounded-[5px] border border-border bg-card py-1.5 pl-3 pr-3 text-left text-[13px] text-sidebar-text transition-colors hover:bg-sidebar-hover"
          >
            <span className="flex items-center gap-2">
              <Search className="h-4 w-4 flex-shrink-0 text-sidebar-text" />
              <span className="flex-1 truncate">Search</span>
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] font-medium text-sidebar-text">
                ⌘K
              </span>
            </span>
          </button>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-3 py-1">
        <SidebarNavItem
          icon={Home}
          label="Home"
          active={isPathActive("headquarters")}
          onClick={() => navigate(`/home/${params.workspaceId}/headquarters`)}
          collapsed={collapsed}
        />

        <SidebarNavItem
          icon={Bot}
          label="ORA"
          active={isPathActive("ai-chat")}
          onClick={() => navigate(`/home/${params.workspaceId}/ai-chat`)}
          collapsed={collapsed}
        />

        <SidebarNavItem
          icon={Mail}
          label="Get started"
          active={isPathActive("get-started")}
          onClick={() => navigate(`/home/${params.workspaceId}/get-started`)}
          collapsed={collapsed}
        />

        {!collapsed ? <div className="my-3" /> : null}

        <SidebarSection title="Workspace" collapsed={collapsed}>
          <SidebarNavItem
            icon={Building2}
            label="Accounts"
            active={currentEntityType === "accounts" || isViewActive(accountsViewId)}
            onClick={() => navigateToView(accountsViewId)}
            collapsed={collapsed}
          />

          <SidebarNavItem
            icon={Calendar}
            label="Meetings"
            active={isPathActive("meetings")}
            onClick={() => navigate(`/home/${params.workspaceId}/meetings`)}
            collapsed={collapsed}
          />

          <SidebarNavItem
            icon={Users}
            label="Contacts"
            active={currentEntityType === "users" || isViewActive(contactsViewId)}
            onClick={() => navigateToView(contactsViewId)}
            collapsed={collapsed}
          />

          <SidebarNavItem
            icon={Activity}
            label="Health"
            active={isPathActive("health")}
            onClick={() => navigate(`/home/${params.workspaceId}/health`)}
            collapsed={collapsed}
          />

          <SidebarNavItem
            icon={CheckSquare}
            label="Tasks"
            active={isPathActive("tasks")}
            onClick={() => navigate(`/home/${params.workspaceId}/tasks`)}
            collapsed={collapsed}
          />
        </SidebarSection>

        <SidebarSection title="Activities" collapsed={collapsed}>
          <SidebarNavItem
            icon={Zap}
            label="Signals"
            active={isPathActive("signals")}
            onClick={() => navigate(`/home/${params.workspaceId}/signals`)}
            collapsed={collapsed}
          />

          <SidebarNavItem
            icon={Wand2}
            label="Skills"
            active={isPathActive("skills")}
            onClick={() => navigate(`/home/${params.workspaceId}/skills`)}
            collapsed={collapsed}
          />

          <SidebarNavItem
            icon={Inbox}
            label="Feed"
            active={isPathActive("inbox")}
            onClick={() => navigate(`/home/${params.workspaceId}/inbox`)}
            collapsed={collapsed}
          />

          <SidebarNavItem
            icon={Download}
            label="Exports"
            active={isPathActive("exports")}
            onClick={() => navigate(`/home/${params.workspaceId}/exports`)}
            collapsed={collapsed}
          />
        </SidebarSection>
      </nav>

      <div className="border-t border-border px-3 py-3">
        <button
          type="button"
          title="Help"
          aria-label="Help"
          onClick={() => window.open("https://hyperengage.com/help", "_blank")}
          className={`h-7 w-7 rounded-full text-muted-foreground transition-colors hover:bg-sidebar-hover hover:text-foreground ${
            collapsed ? "mx-auto flex items-center justify-center" : "flex items-center justify-center"
          }`}
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      </div>

      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-9 z-50 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm transition-colors hover:bg-sidebar-hover hover:text-foreground"
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>
    </aside>
  );
};

export default LeftSideBar;
